/**
 * Self-healing media for the user's OWN avatars.
 *
 * Rows created by the previously-deployed lambda have no stored
 * preview_image_url, so cards would render blank. This helper fetches the
 * avatar's status once (the deployed status endpoint returns the group's looks,
 * whose first look carries the image + usable avatar_id) and caches the result
 * for the session. After the backend redeploy, rows carry the preview directly
 * and this is only a fallback.
 */
import { v2Service } from '@/api/v2Service';

export interface OwnedAvatarMedia {
  img?: string;
  lookId?: string;
  status?: string;
}

const cache = new Map<string, OwnedAvatarMedia>();
const inFlight = new Map<string, Promise<OwnedAvatarMedia>>();

export function resolveOwnedAvatarMedia(userId: string, avatarRef: string): Promise<OwnedAvatarMedia> {
  const hit = cache.get(avatarRef);
  if (hit) return Promise.resolve(hit);
  const pending = inFlight.get(avatarRef);
  if (pending) return pending;

  const p = (async () => {
    try {
      const st = await v2Service.avatarStatus(userId, avatarRef);
      const media: OwnedAvatarMedia = {
        img: st.preview_image_url || st.looks?.[0]?.preview_image_url || undefined,
        lookId: st.avatar_id || st.looks?.[0]?.avatar_id || undefined,
        status: st.status,
      };
      if (media.img || media.lookId) cache.set(avatarRef, media); // cache successes only
      return media;
    } catch {
      return {};
    } finally {
      inFlight.delete(avatarRef);
    }
  })();
  inFlight.set(avatarRef, p);
  return p;
}
